import React, { useMemo, useEffect } from 'react';

import { InputFieldAddOnType } from '@zola/zola-ui/src/components/Form/InputFieldV2/InputFieldAddOn';
import FinalFormInput from '@zola/zola-ui/src/components/Form/inputV3/FinalFormInput';
import { FinalFormInputProps } from '@zola/zola-ui/src/components/Form/inputV3/FinalFormInput/FinalFormInput';

import { FieldState, FieldValidator, FormApi } from 'final-form';
import { FieldRenderProps, useForm, useFormState } from 'react-final-form';

import { maxIntegerValue, MONEY, REQUIRED } from '~/components/common/form/commonValidations';
import { formatStringToNumber } from '~/util/formatters';
import { formatWithCommas } from '~/util/textUtils';

/**
 * Normalizes the input value (strips commas for instance) then validates the
 * normalized value.
 *
 * @param normalizer
 * @param validators An array of validators to check for valid input
 * @returns A Field Validator for react final form
 */
const normalizeAndValidate =
  (normalizer: (rawValue: string) => string, validators: FieldValidator<string>[]) =>
  (value: string, allValues: unknown, meta?: FieldState<string>) => {
    const normalValue = normalizer(value);

    return validators.reduce((error, validator) => {
      return error || validator(normalValue, allValues as object, meta);
    }, undefined);
  };

/** Removes commas from a number */
const normalizeNumberWithCommas = (value: string) => {
  const parsed = formatStringToNumber(value);
  return parsed ? `${parsed}` : '';
};

/** Turns a number into a string with commas in the right places */
export const formatNumberWithCommas = (value: any) => {
  return formatWithCommas(formatStringToNumber(value));
};

export type NumberInputWithCommasProps<
  FormValues = Record<string, any>,
  T extends HTMLElement = HTMLElement,
  RP extends FieldRenderProps<string, T> = FieldRenderProps<string, T>
> = {
  /** Array of validators to apply to the input */
  validators?: FieldValidator<string>[];
  /**
   * The name of the input that is displayed on the screen.  This field accepts
   * commas.  Using form mutators, this component strips out the commas (normalizes)
   * in a different form field.
   *
   * @example
   * weddingBudgetAsCurrency
   *
   * @see NumberInputWithCommas
   */
  displayedField: keyof FormValues;

  /**
   * Name of the form field that stores the normalized (no commas) version of
   * the input
   *
   * @example
   * weddingBudget
   *
   * @see NumberInputWithCommas
   */
  name: keyof FormValues;

  /**
   * Type of this field. If currency is used, the CASH input add on will automatically
   * be applied and the value will be limited to a maximum of 10,000,00.
   *
   * Defaults to currency.
   */
  type?: 'number' | 'currency';
} & Omit<FinalFormInputProps<RP, T>, 'name' | 'validate' | 'type'>;

type ValueOf<T> = T[keyof T];

type SetFieldMutator<FormValues = Record<string, any>> = (
  ...args: [field: keyof FormValues, value: ValueOf<FormValues>]
) => void;

interface FormWithMutators<FormValues = Record<string, any>> extends FormApi<FormValues> {
  mutators: {
    setField: SetFieldMutator<FormValues>;
  };
}

/**
 * A wrapper around react final-form inputs that are values with commas (such as
 * how much do you want to spend on your photographer, or what is your overall
 * wedding budget).  This wrapper allows for the user to type both numbers and
 * commas, and when the input is unfocused, formats whatever they typed.
 *
 * This component requires:
 * - Use in react-final-form
 * - A form with a mutator named setField
 *
 * This component:
 * - Uses two fields in the form values.  One field (name) is the value of the
 *   input when commas have been stripped out.  For instance, the user types
 *   in "5,000" then the value is "5000".  This is the field you want to use
 *   for validation, and passing to backend api requests, etc.  The second field
 *   is what is displayed.  This field can include commas (and probably will).
 *   The user can type commas to their heart's content, and then when they blur
 *   out of the field, we strip out the commas and re-format the input appropriately.
 *
 */
export const NumberInputWithCommas = <
  FormValues extends Record<string, any>,
  FormType extends FormWithMutators<FormValues>,
  T extends HTMLElement = HTMLElement,
  RP extends FieldRenderProps<string, T> = FieldRenderProps<string, T>
>({
  validators = [],
  label,
  name,
  displayedField,
  type,
  required,
  ...rest
}: NumberInputWithCommasProps<FormValues, T, RP>): JSX.Element => {
  const fieldProps = {
    /**
     * When the input field blurs (loses focus), format the value, so if they had
     * typed 2000, it becomes 2,000.  If they had typed 2,00,00 it becomes 20,000.
     */
    format: formatNumberWithCommas,
    formatOnBlur: true,
    parse: (value: string) => {
      // Let them type any number or comma
      if (/^[0-9,]*$/.test(value)) {
        return value;
      }
      // If they type something else, format what they've typed so far
      return normalizeNumberWithCommas(value);
    },
    addOn: type === 'currency' ? InputFieldAddOnType.CASH : undefined,
    type: type === 'currency' ? 'text' : 'numeric',

    /**
     * When the input loses focus, push the cursor to the end of the field.
     * This really is only noticeable in the simulator when you tap somewhere
     * else on the screen and the keyboard remains up.  Otherwise, the cursor
     * isn't visible.  We need to push the cursor to the end because the
     * formatting may add commas
     */
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
      // This needs to be in a timeout so the formatOnBlur occurs first.
      setTimeout(() => {
        const { target } = event;
        const inputLength = target.value.length;
        if ((target.selectionStart || 0) < inputLength) {
          target.setSelectionRange(inputLength, inputLength);
          target.blur();
        }
      }, 50);
    },
  };

  const validate = useMemo(() => {
    const typeValidators = type === 'currency' ? [maxIntegerValue(10_000_000), MONEY] : [];
    const requiredValidator = required ? [REQUIRED] : [];
    return normalizeAndValidate(normalizeNumberWithCommas, [
      ...validators,
      ...requiredValidator,
      ...typeValidators,
    ]);
  }, [required, type, validators]);

  const { mutators } = useForm<FormValues>() as FormType;
  if (typeof mutators === 'undefined' || typeof mutators.setField === 'undefined') {
    throw new Error(
      'NumberInputWithCommas must be used within a form that supports the setField api'
    );
  }

  const { setField } = mutators;
  const { initialValues, values } = useFormState<FormValues>();

  // On initialization, set the displayed value to the formatted version of the
  // value in the form (a string representing a number, or a number)
  useEffect(() => {
    // Nothing in the initial values, nothing in the current values: we need to initialize.
    if (!initialValues[displayedField] && !values[displayedField]) {
      setField(displayedField, formatNumberWithCommas(initialValues[name]) as ValueOf<FormValues>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // Current value of the displayed value in the form, this is a string, possibly
  // with commas in it.
  const displayedValue = values[displayedField];

  // When the displayed value changes, update the real value in the form
  useEffect(() => {
    setField(name, normalizeNumberWithCommas(displayedValue as string) as ValueOf<FormValues>);
  }, [name, setField, displayedValue]);

  return (
    <FinalFormInput
      label={label}
      validate={validate}
      name={displayedField as string}
      inputMode="numeric"
      allowChangeToEmpty
      {...fieldProps}
      {...rest}
    />
  );
};
